import { useState } from "react";
import TaskCard from "./TaskCard";
import { formatDate, groupByDate } from "../utils/tasks";
import { AlertTriangle, CalendarDays, Check, Flag } from "lucide-react";
import { InlineIcon } from "./Icon";

export default function TimelineView({ tasks, event, onOpen, onStatus, onEditContractor }) {
  const [collapsed, setCollapsed] = useState({});
  const toggle = (key) => setCollapsed((p) => ({ ...p, [key]: !p[key] }));
  const { groups, dateKeys, withoutDate } = groupByDate(tasks);

  const renderGroup = (key, groupTasks, label, accentColor) => {
    const isCollapsed = collapsed[key];
    const doneCount = groupTasks.filter((t) => t.status === "done").length;
    const problemCount = groupTasks.filter((t) => t.status === "problem").length;
    const sorted = [...groupTasks].sort((a, b) =>
      (a.time || "").localeCompare(b.time || "")
    );

    return (
      <div key={key} style={{ marginBottom: 20 }}>
        <button
          onClick={() => toggle(key)}
          style={{
            width: "100%",
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "0 0 12px 0",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <div
            style={{
              background: accentColor,
              color: "var(--color-bg-card)",
              padding: "6px 14px",
              borderRadius: "var(--radius-full)",
              fontSize: "var(--text-xs)",
              fontWeight: "var(--font-medium)",
              flexShrink: 0,
              textTransform: "capitalize",
              whiteSpace: "nowrap",
            }}
          >
            <InlineIcon Icon={CalendarDays} style={{ marginRight: 4 }} />
            {label}
          </div>
          <div style={{ display: "flex", gap: 5, flexShrink: 0 }}>
            <span
              style={{
                background: "var(--color-bg-hover)",
                color: "var(--color-text-muted)",
                padding: "2px 8px",
                borderRadius: "var(--radius-full)",
                fontSize: "var(--text-xs)",
                fontWeight: "var(--font-medium)",
              }}
            >
              {groupTasks.length}{" "}
              {groupTasks.length === 1
                ? "zadanie"
                : groupTasks.length < 5
                ? "zadania"
                : "zadań"}
            </span>
            {doneCount > 0 && (
              <span
                style={{
                  background: "var(--color-success-bg)",
                  color: "var(--color-success)",
                  padding: "2px 8px",
                  borderRadius: "var(--radius-full)",
                  fontSize: "var(--text-xs)",
                  fontWeight: "var(--font-medium)",
                }}
              >
                <InlineIcon Icon={Check} style={{ marginRight: 4 }} />
                {doneCount}
              </span>
            )}
            {problemCount > 0 && (
              <span
                style={{
                  background: "var(--color-danger-bg)",
                  color: "var(--color-danger)",
                  padding: "2px 8px",
                  borderRadius: "var(--radius-full)",
                  fontSize: "var(--text-xs)",
                  fontWeight: "var(--font-medium)",
                }}
              >
                <InlineIcon Icon={AlertTriangle} style={{ marginRight: 4 }} />
                {problemCount}
              </span>
            )}
          </div>
          <div style={{ flex: 1, height: 1, background: "var(--color-border)" }} />
          <span style={{ color: "var(--color-text-muted)", fontSize: "var(--text-sm)" }}>
            {isCollapsed ? "▸" : "▾"}
          </span>
        </button>

        {!isCollapsed && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            {sorted.map((task) => (
              <div key={task.id}>
                <TaskCard
                  task={task}
                  onOpen={() => onOpen(task)}
                  onStatus={(s) => onStatus(task.id, s)}
                  onEditContractor={onEditContractor}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderMilestone = (key, label, color) => (
    <div key={key} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
      <div
        style={{
          background: color,
          color: "var(--color-bg-card)",
          padding: "6px 14px",
          borderRadius: "var(--radius-full)",
          fontSize: "var(--text-xs)",
          fontWeight: "var(--font-medium)",
          flexShrink: 0,
          whiteSpace: "nowrap",
        }}
      >
        <InlineIcon Icon={Flag} style={{ marginRight: 4 }} />
        {label}
      </div>
      <div style={{ flex: 1, height: 2, background: color, opacity: 0.35 }} />
    </div>
  );

  const milestones = [];
  if (event?.start_date) milestones.push({ date: event.start_date, key: "milestone-start", label: `Początek: ${formatDate(event.start_date)}`, color: "var(--color-text-muted)" });
  if (event?.end_date) milestones.push({ date: event.end_date, key: "milestone-end", label: `Koniec: ${formatDate(event.end_date)}`, color: "var(--color-text-muted)" });

  const allKeys = [...new Set([...dateKeys, ...milestones.map((m) => m.date)])].sort();

  return (
    <div>
      {allKeys.map((key) => {
        const ms = milestones.filter((m) => m.date === key);
        const groupTasks = groups[key];
        return (
          <div key={key}>
            {ms.map((m) => renderMilestone(m.key, m.label, m.color))}
            {groupTasks && renderGroup(key, groupTasks, formatDate(key), "var(--color-text-primary)")}
          </div>
        );
      })}
      {withoutDate.length > 0 &&
        renderGroup("bez-terminu", withoutDate, "Bez terminu", "var(--color-text-muted)")}
    </div>
  );
}
